import React, { useEffect, useState } from "react";
import axios from "../../api";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Button,
} from "@mui/material";
import { Close, OpenInNew } from "@mui/icons-material";

/**
 * Inline gallery for undertaking uploads (ID proof, signature, laptop/asset photos).
 * Loads short-lived S3 URLs via /undertaking/file-url.
 */
function UndertakingDocuments({
  idProofPath,
  signaturePath,
  assetImages = [],
  title = "Uploaded Documents",
}) {
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const keys = [
    idProofPath ? { key: idProofPath, label: "ID Proof", kind: "id" } : null,
    signaturePath
      ? { key: signaturePath, label: "Signature", kind: "signature" }
      : null,
    ...(Array.isArray(assetImages)
      ? assetImages.filter(Boolean).map((k, i) => ({
          key: k,
          label: `Laptop / Asset photo ${i + 1}`,
          kind: "asset",
        }))
      : []),
  ].filter(Boolean);

  useEffect(() => {
    let cancelled = false;
    if (!keys.length) {
      setUrls({});
      return undefined;
    }
    (async () => {
      setLoading(true);
      setError("");
      const next = {};
      try {
        await Promise.all(
          keys.map(async ({ key }) => {
            try {
              const resp = await axios.get("/undertaking/file-url", {
                params: { key },
              });
              if (resp.data?.url) next[key] = resp.data.url;
            } catch {
              next[key] = null;
            }
          })
        );
        if (!cancelled) setUrls(next);
      } catch (e) {
        if (!cancelled) setError("Could not load document previews.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-fetch when the set of keys changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProofPath, signaturePath, JSON.stringify(assetImages || [])]);

  if (!keys.length) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No ID proof, signature, or asset photos were uploaded with this
          submission.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading previews…
          </Typography>
        </Box>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      <Grid container spacing={2}>
        {keys.map(({ key, label }) => {
          const url = urls[key];
          return (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {label}
              </Typography>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "grey.50",
                  minHeight: 160,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: url ? "pointer" : "default",
                }}
                onClick={() => url && setLightbox({ url, label })}
              >
                {url === undefined && loading ? (
                  <CircularProgress size={24} />
                ) : url ? (
                  <Box
                    component="img"
                    src={url}
                    alt={label}
                    sx={{
                      width: "100%",
                      maxHeight: 220,
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ p: 2 }}>
                    Preview unavailable
                  </Typography>
                )}
              </Box>
              {url && (
                <Button
                  size="small"
                  startIcon={<OpenInNew />}
                  sx={{ mt: 0.5 }}
                  onClick={() => window.open(url, "_blank", "noopener")}
                >
                  Open full size
                </Button>
              )}
            </Grid>
          );
        })}
      </Grid>

      <Dialog
        open={Boolean(lightbox)}
        onClose={() => setLightbox(null)}
        maxWidth="lg"
        fullWidth
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton onClick={() => setLightbox(null)} aria-label="Close">
            <Close />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="subtitle1" gutterBottom>
            {lightbox?.label}
          </Typography>
          {lightbox?.url && (
            <Box
              component="img"
              src={lightbox.url}
              alt={lightbox.label}
              sx={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default UndertakingDocuments;
